import React, { useContext, useEffect, useState } from 'react';
import { Grid, useMediaQuery, useTheme, styled } from '@material-ui/core';
import { SdmTypography, SdmSpacer, SdmTextButton } from 'components';
import PageHeader from 'pages/breakout/components/PageHeader';
import { useIntl } from 'react-intl';
import { theme } from 'theme';
import translations from 'utils/translations';
import FormDialog from './SalesTeam/Dialogs/FormDialog';
import {
  createSaleMember,
  deleteSaleMember,
  updateSaleMember,
} from 'entities/salesTeam/salesTeamHelper';
import { LoaderContext } from 'context/LoaderContextProvider';
import SalesMemberCard from './SalesTeam/SalesMemberCard';
import DeleteDialog from './SalesTeam/Dialogs/DeleteDialog';
import { getBrand } from 'entities/agenda/agendaHelper';

const CardContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '90%',
  margin: '0 auto',
  maxWidth: 1063,
  paddingBottom: 64,
});

const SalesTeam = ({ loggedUser }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Agenda(intl).salesTeam.page;
  const dialogi18n = translations.Agenda(intl).salesTeam.dialog;
  const { setLoaderActive, isLoaderActive } = useContext(LoaderContext);
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  // States
  const [brand, setBrand] = useState();
  const [needToRemove, setNeedToRemove] = useState();
  const [formDialog, setFormDialog] = useState({
    isOpen: false,
    selectedMember: {},
    context: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
  });
  const mediaTheme = useTheme();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));

  // Effects
  useEffect(() => {
    if (!brand) fetchSalesMember();
  }, [brand]);

  useEffect(() => {
    if (needToRemove) {
      setDeleteDialog({ isOpen: true });
    }
  }, [needToRemove]);

  // Functions
  const addReferent = () => setFormDialog({ isOpen: true });

  const fetchSalesMember = async () => {
    showLoader();
    const params = new URLSearchParams(location.search);
    const id = params.get('salesforce_id');
    if (id) {
      const _brand = await getBrand({ id });
      setBrand(_brand);
    } else if (loggedUser?.salesforceIdBrand) {
      const _brand = await getBrand({ id: loggedUser.salesforceIdBrand });
      setBrand(_brand);
    }
    hideLoader();
  };

  const handleSubmit = async (data) => {
    showLoader();
    setFormDialog({ isOpen: false, context: '', selectedMember: {} });
    const input = { ...data, salesMemberBrandId: brand.id };
    let res = {};
    if (input.id) {
      res = await updateSaleMember({
        showLoader,
        hideLoader,
        input,
      });
    } else {
      res = await createSaleMember({
        showLoader,
        hideLoader,
        input,
      });
    }
    if (res?.id) setBrand();
    hideLoader();
  };

  const handleForm = (id, context) =>
    setFormDialog({
      isOpen: true,
      selectedMember: brand?.salesTeam?.items.find((m) => m.id === id),
      context: context,
    });

  const handleDeleteAlert = (id) => {
    setFormDialog({ isOpen: false, context: '', selectedMember: {} });
    setNeedToRemove(id);
  };

  const handleDelete = async () => {
    showLoader();
    setDeleteDialog({ isOpen: false });
    const input = { id: needToRemove };
    const deleted = await deleteSaleMember({
      input,
    });
    if (deleted.id) setBrand();
    setNeedToRemove();
    hideLoader();
  };

  // Renders
  const renderFormDialog = () => {
    if (!formDialog.isOpen) return null;
    return (
      <FormDialog
        onClose={() => {
          setFormDialog({ isOpen: false, context: '', selectedMember: {} });
        }}
        onAgree={(data) => handleSubmit(data)}
        onDelete={handleDeleteAlert}
        i18n={dialogi18n}
        isSmDown={isSmDown}
        data={formDialog}
      />
    );
  };
  const renderDeleteDialog = () => {
    if (!deleteDialog.isOpen) return null;
    return (
      <DeleteDialog
        onClose={() => {
          setDeleteDialog({ isOpen: false });
          setNeedToRemove();
        }}
        onAgree={() => handleDelete()}
        i18n={dialogi18n.delete}
        isSmDown={isSmDown}
      />
    );
  };
  return (
    <Grid container>
      <div style={{ width: '100%' }}>
        {renderFormDialog()}
        {renderDeleteDialog()}
        <PageHeader
          i18n={brand?.name ? { ...i18n, title: `${i18n.title}` } : i18n}
          isBrandPage={false}
        />
        <div
          style={{
            width: isSmDown ? '90%' : '80%',
            margin: '0 auto',
            maxWidth: 830,
          }}
        >
          {brand ? (
            <SdmTypography
              variant={isSmDown ? 'h4Regular' : 'h2Regular'}
              style={{
                width: '100%',
                color: theme.colors.primary.black,
              }}
              textAlign="center"
            >
              {brand?.name ? (
                <>
                  {i18n.textWithBrand}{' '}
                  <span style={{ fontWeight: 'bold' }}>{brand?.name}</span>
                </>
              ) : (
                i18n.text
              )}
            </SdmTypography>
          ) : null}
          {brand && !brand?.salesTeam?.items.length && !isLoaderActive ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <SdmSpacer height={60} />
              <SdmTypography
                variant={isSmDown ? 'h4' : 'h1'}
                style={{
                  width: '100%',
                  color: theme.colors.primary.black,
                }}
              >
                {i18n.noContacts}
              </SdmTypography>
              <SdmTypography
                variant={isSmDown ? 'h5' : 'h2'}
                style={{
                  width: '100%',
                  color: theme.colors.primary.black,
                }}
              >
                {i18n.addMsg}
              </SdmTypography>
              <SdmSpacer height={20} />
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <SdmSpacer height={20} />
            <SdmTextButton
              height={46}
              textVariant={isSmDown ? 'h4Regular' : 'h2Regular'}
              text={i18n.btn}
              variant="redFill"
              onClick={() => addReferent()}
            />
          </div>
        </div>
        <SdmSpacer height={70} />
      </div>
      {!isLoaderActive && brand?.salesTeam?.items.length ? (
        <Grid item xs={12}>
          <div style={{ backgroundColor: '#F3F3F3' }}>
            <CardContainer>
              {brand?.salesTeam?.items.length
                ? brand?.salesTeam?.items.map((s, idx) => (
                    <SalesMemberCard
                      key={idx}
                      member={s}
                      i18n={i18n.card}
                      isSmDown={isSmDown}
                      onView={(id) => handleForm(id, 'view')}
                      onEdit={(id) => handleForm(id, 'edit')}
                      onDelete={(id) => setNeedToRemove(id)}
                    />
                  ))
                : null}
            </CardContainer>
          </div>
        </Grid>
      ) : null}
    </Grid>
  );
};
export default SalesTeam;
